import { Head, Link } from '@inertiajs/react'
import { BookOpen } from 'lucide-react'
import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { buttonClasses } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

export type TeamPlayersShowProps = {
  player: {
    id: string
    name: string
    prefecture?: string | null
    birthday?: string | null
    height?: number | null
    fatherHeight?: number | null
    motherHeight?: number | null
    fiftyMeterTime?: number | null
    boarding: boolean
    comment?: string | null
    dominantFoot?: string | null
    dominantFootLabel?: string | null
    positions: string[]
    videos: string[]
    updatedAt: string
  }
  breadcrumbs: Breadcrumb[]
}

export default function TeamPlayersShow({ player, breadcrumbs }: TeamPlayersShowProps) {
  return (
    <AppShell>
      <Head title={`${player.name ?? '選手詳細'} | 所属選手`} />
      <PageHeader
        title={player.name ?? '選手詳細'}
        description="選手のプロフィールを確認し、最新の情報にアップデートしましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <Link href="/team/players" className="text-sm text-brand-300 hover:text-brand-100 transition">
            一覧に戻る
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_1fr]">
        <Card className="space-y-6">
          <Section title="基本情報">
            <InfoGrid
              items={[
                { label: '氏名', value: player.name ?? '未入力' },
                { label: '都道府県', value: player.prefecture ?? '未入力' },
                { label: '生年月日', value: formatDate(player.birthday) },
              ]}
            />
          </Section>

          <Section title="身体情報">
            <InfoGrid
              items={[
                { label: '身長', value: toCm(player.height) },
                { label: '父親の身長', value: toCm(player.fatherHeight) },
                { label: '母親の身長', value: toCm(player.motherHeight) },
                { label: '50m走', value: player.fiftyMeterTime ? `${player.fiftyMeterTime.toFixed(2)} 秒` : '未入力' },
              ]}
            />
          </Section>

          <Section title="競技情報">
            <InfoGrid
              items={[
                { label: 'ポジション', value: player.positions.length ? player.positions.join(', ') : '未選択' },
                { label: '利き足', value: player.dominantFootLabel || '未選択' },
                { label: '下宿可否', value: player.boarding ? '下宿可能' : '下宿不可' },
              ]}
            />
          </Section>

          <Section title="自己PR">
            <p className="text-sm text-white/80 whitespace-pre-wrap">
              {player.comment && player.comment.trim().length > 0 ? player.comment : 'コメントは登録されていません。'}
            </p>
          </Section>

          <Section title="ハイライト動画">
            {player.videos.length === 0 ? (
              <p className="text-sm text-white/60">動画は登録されていません。</p>
            ) : (
              <ul className="space-y-2 text-sm text-brand-300">
                {player.videos.map((url, index) => (
                  <li key={`${url}-${index}`}>
                    <a href={url} target="_blank" rel="noreferrer" className="hover:text-brand-100">
                      動画 {index + 1}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </Card>

        <div className="space-y-6">
          <Card className="space-y-4">
            <h2 className="text-sm font-semibold text-white/80">サッカー日記</h2>
            <p className="text-sm text-white/60">日々の練習や試合の振り返りを確認できます</p>
            <Link
              href={`/team/players/${player.id}/soccer_diaries`}
              className={buttonClasses({ variant: 'primary', size: 'sm' })}
            >
              <BookOpen className="mr-2 h-4 w-4" />
              日記を見る
            </Link>
          </Card>

          <Card className="space-y-4">
            <Section title="メタ情報">
              <InfoGrid
                items={[
                  { label: '最終更新', value: formatDateTime(player.updatedAt) },
                  { label: 'ポジション数', value: `${player.positions.length} 件` },
                ]}
              />
            </Section>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-white/80">{title}</h2>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white">
        {children}
      </div>
    </div>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: string }> }) {
  return (
    <dl className="space-y-3">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-1">
          <dt className="text-xs text-white/40">{item.label}</dt>
          <dd className="text-sm text-white">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function toCm(value?: number | null) {
  if (value == null) return '未入力'
  return `${value} cm`
}

function formatDate(value?: string | null) {
  if (!value) return '未入力'
  return new Date(value).toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
